import { useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const EVENT_COLORS = {
  LOGIN_SUCCESS:        "#22c55e",
  LOGIN_FAILED:         "#ef4444",
  ACCOUNT_LOCKED:       "#f97316",
  LOGOUT:               "#94a3b8",
  PASSWORD_CHANGE:      "#3b82f6",
  TOKEN_REFRESH:        "#6366f1",
  ADMIN_ACTION:         "#a855f7",
  PERMISSION_CHANGE:    "#ec4899",
  SUSPICIOUS_ACTIVITY:  "#dc2626",
};

function StatCard({ label, value, color, theme: t }) {
  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, padding: "16px 20px" }}>
      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || t.text }}>{value}</div>
    </div>
  );
}

export default function SecurityDashboard({ theme: t }) {
  const [events, setEvents]   = useState([]);
  const [stats, setStats]     = useState(null);
  const [tokens, setTokens]   = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [evRes, stRes, tkRes] = await Promise.all([
        fetchWithAuth(`${API}/api/security/events?limit=50`),
        fetchWithAuth(`${API}/api/security/stats`),
        fetchWithAuth(`${API}/api/security/token-health`),
      ]);
      if (evRes.ok) { const d = await evRes.json(); setEvents(d.events || []); }
      if (stRes.ok) { const d = await stRes.json(); setStats(d); }
      if (tkRes.ok) { const d = await tkRes.json(); setTokens(d.tokens || {}); }
    } catch {
      setError("Failed to load security data");
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const badge = (status) => {
    const colors = { healthy: "#22c55e", "expiring-soon": "#f97316", expired: "#ef4444", unknown: "#94a3b8" };
    return <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, background: colors[status] || "#94a3b8", color: "#fff", fontWeight: 600 }}>{status}</span>;
  };

  return (
    <div style={{ padding: "0 0 32px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: t.text }}>Security Dashboard</div>
          <div style={{ fontSize: 12, color: t.textMuted }}>Real-time security monitoring — super admin only</div>
        </div>
        <button
          onClick={fetchAll}
          style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${t.border}`, background: "transparent", color: t.text, cursor: "pointer", fontSize: 12 }}
        >
          Refresh
        </button>
      </div>

      {error && <div style={{ padding: "10px 14px", background: "#7f1d1d", borderRadius: 8, color: "#fca5a5", marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {/* Stats grid */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
          <StatCard label="Logins (24h)"       value={stats.last24h.loginSuccess}   color="#22c55e" theme={t} />
          <StatCard label="Failed Logins (24h)" value={stats.last24h.loginFailed}    color="#ef4444" theme={t} />
          <StatCard label="Locked Accounts"     value={stats.last24h.accountLocked}  color="#f97316" theme={t} />
          <StatCard label="Pw Changes (24h)"    value={stats.last24h.passwordChanges} color="#3b82f6" theme={t} />
          <StatCard label="Admin Actions (24h)" value={stats.last24h.adminActions}   color="#a855f7" theme={t} />
          <StatCard label="Suspicious (24h)"    value={stats.last24h.suspicious}     color="#dc2626" theme={t} />
          <StatCard label="Token Blacklist"     value={stats.blacklistSize}           color={t.textMuted} theme={t} />
        </div>
      )}

      {/* Facebook token health */}
      {Object.keys(tokens).length > 0 && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, padding: 16, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 12 }}>Facebook Token Health</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            {Object.entries(tokens).map(([pub, info]) => (
              <div key={pub} style={{ background: t.bg, borderRadius: 6, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: t.text, textTransform: "capitalize" }}>{pub}</span>
                  {badge(info.status)}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                  Expires: {info.expires}<br />
                  {info.daysLeft != null ? `${info.daysLeft} days left` : "Unknown expiry"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent security events */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 12 }}>
          Recent Security Events {events.length > 0 && <span style={{ color: t.textMuted, fontWeight: 400, fontSize: 11 }}>({events.length} shown)</span>}
        </div>
        {loading ? (
          <div style={{ color: t.textMuted, fontSize: 13, padding: "20px 0", textAlign: "center" }}>Loading…</div>
        ) : events.length === 0 ? (
          <div style={{ color: t.textMuted, fontSize: 13, padding: "20px 0", textAlign: "center" }}>No events recorded yet</div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                  {["Time", "Event", "Email", "IP"].map(h => (
                    <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: t.textMuted, fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${t.border}20` }}>
                    <td style={{ padding: "6px 8px", color: t.textMuted, whiteSpace: "nowrap" }}>
                      {new Date(ev.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </td>
                    <td style={{ padding: "6px 8px" }}>
                      <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: (EVENT_COLORS[ev.event] || "#94a3b8") + "22", color: EVENT_COLORS[ev.event] || "#94a3b8", fontWeight: 600 }}>
                        {ev.event}
                      </span>
                    </td>
                    <td style={{ padding: "6px 8px", color: t.text, fontFamily: "monospace" }}>{ev.email || "—"}</td>
                    <td style={{ padding: "6px 8px", color: t.textMuted, fontFamily: "monospace" }}>{ev.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
